import classNames from "classnames";
import FilterLine from "component/category/FilterLine";
import PAButton, {
  BUTTON_SIZE,
  BUTTON_TYPE,
} from "component/design-system/buttons/PAButton";
import { useRouter } from "next/router";
import qs from "query-string";
import { ReactNode, useEffect, useState } from "react";
import HideIf from "util/HideIf";
import { getCategoryAggregation } from "../../../../api/category/getCategoryAggregation";
import { isEmpty } from "../../../../util/lodash/isEmpty";
import stylesV2 from "../../category-tree-v2.module.scss";
import RenderLink from "../RenderLink";
import styles from "./product-filter.module.scss";

interface Props {
  isCategoryOpen: boolean;
  activeCategory: boolean;
  activeParent: boolean;
  item: any;
  href: string;
  asPath: string;
  children: ReactNode;
  tier1?: string;
  isTier1?: boolean;
  isTier2?: boolean;
}

function CategoryToggler(props: Props) {
  const {
    isCategoryOpen,
    activeCategory,
    activeParent,
    item,
    href,
    asPath,
    setCategoryAggData,
    className = "",
    rightLineGap,
    bottomLineGap,
    setIsCategoryTreeClick,
    setParallelCategoryAggData,
    tier0Url,
    tier0,
    children,
    tier1,
    isTier1 = false,
    isTier2 = false,
  } = props;

  const router = useRouter();
  const { asPath: currentAsPath } = router;
  const { pathname } = router.query;
  const isShopByCategory = currentAsPath === "/all-parts";

  const [isOpen, setIsOpen] = useState(isCategoryOpen);

  useEffect(() => {
    setIsOpen(isCategoryOpen);
  }, [isCategoryOpen]);

  function getCategoryApiParams() {
    const category = {
      t0: tier0,
      t1: undefined,
      t2: undefined,
      t3: undefined,
    };

    if (isTier1) {
      category.t1 = item.bucket;
    }

    if (isTier2) {
      category.t1 = tier1;
      category.t2 = item.bucket;
    }
    return category;
  }

  function setCategories(isParallelCategory: boolean) {
    const categories = getCategoryApiParams();

    const [url = "", q = {}] = asPath.split("?");
    const {
      tier1: t1,
      tier2: t2,
      tier3: t3,
      availabilities: availability,
      prices: priceRanges,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      selectedMake,
      selectedModel,
      selectedYear,
      ...query
    } = qs.parse(q);
    const category = isParallelCategory
      ? { t0: tier0 }
      : categories;

    if (availability) {
      query.availability = availability;
    }
    if (priceRanges) {
      query.priceRanges = priceRanges;
    }
    getCategoryAggregation(
      isParallelCategory ? tier0Url || pathname : url,
      category,
      query,
    ).then(({ data }) => {
      // eslint-disable-next-line no-unused-expressions
      isParallelCategory
        ? setParallelCategoryAggData(data.categories)
        : setCategoryAggData(data.categories);
      setIsOpen(!isOpen);
    });
  }

  function handleArrowClick() {
    if (
      !isOpen
      && isEmpty(item.tier1s)
      && isEmpty(item.tier2s)
      && isEmpty(item.tier3s)
    ) {
      setCategories(false);
    } else if (setParallelCategoryAggData && !isShopByCategory) {
      setCategories(true);
    } else {
      setIsOpen(!isOpen);
    }
  }

  function ArrowButton() {
    return (
      <PAButton
        className={`m-r-5 ${styles.toggler_button}`}
        buttonType={BUTTON_TYPE.NORMAL}
        size={BUTTON_SIZE.STANDARD}
        onClick={handleArrowClick}
      >
        <i
          className={classNames("fa", styles.caret_button, {
            "fa-minus": isOpen,
            "fa-plus": !isOpen,
          })}
        />
      </PAButton>
    );
  }

  return (
    <div className={`${className} ${styles.parent_category}`}>
      {isOpen && (
        <i
          className={styles.next_right_line}
          style={{
            left: rightLineGap || "calc(1.7 * var(--safari-root-font, 10px))",
            width: "calc(0.6 * var(--safari-root-font, 10px))",
          }}
        />
      )}
      <FilterLine
        className={styles.next_bottom_line}
        left={bottomLineGap || 8}
      />

      <div
        className={classNames("d-flex align-items-center m-b-5", {
          [styles.active_parent]: activeParent && !activeCategory,
          [styles.active_tier]: activeCategory,
          [stylesV2.active_tier_v2]: activeCategory,
        })}
        data-testid={activeCategory ? "active-tier" : "inactive-tier"}
      >
        <ArrowButton />

        <span className={`${styles.bucket_label} ${styles.tier_bucket}`}>
          <RenderLink
            href={href}
            asPath={asPath}
            label={item.bucket}
            title={`Explore ${item.bucket} For Your Car`}
            className={classNames(styles.render_link, stylesV2.render_link_v2)}
            onClick={setIsCategoryTreeClick}
          />
        </span>
        <HideIf show={item.count}>
          <span className={styles.product_count}>
            (
            {item.count}
            )
          </span>
        </HideIf>
      </div>
      {isOpen && children}
    </div>
  );
}

export default CategoryToggler;
